import { Outlet, NavLink, useParams } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useQuery } from '@tanstack/react-query';
import { api } from '../utils/api';
import {
    LayoutDashboard,
    FolderKanban,
    Search,
    Share2,
    Megaphone,
    Mail,
    Wallet,
    BarChart3,
    Settings,
    LogOut,
    Menu,
    X,
    Sparkles,
    Zap,
    Activity
} from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';

export default function DashboardLayout() {
    const { user, logout } = useAuthStore();
    const { projectId } = useParams();
    const [sidebarOpen, setSidebarOpen] = useState(true);

    // Fetch today's usage for the header indicator
    const { data: usageData } = useQuery({
        queryKey: ['usage-today'],
        queryFn: async () => {
            const res = await api.get('/usage/today');
            return res.data.data;
        },
        refetchInterval: 60000, // Refresh every minute
    });

    const mainNav = [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/projects', icon: FolderKanban, label: 'Projects' },
        { to: '/usage', icon: Activity, label: 'Usage' },
        { to: '/settings', icon: Settings, label: 'Settings' },
    ];

    const moduleNav = projectId ? [
        { to: `/projects/${projectId}/seo`, icon: Search, label: 'SEO' },
        { to: `/projects/${projectId}/social`, icon: Share2, label: 'Social Media' },
        { to: `/projects/${projectId}/ads`, icon: Megaphone, label: 'Ads' },
        { to: `/projects/${projectId}/email`, icon: Mail, label: 'Email' },
        { to: `/projects/${projectId}/payments`, icon: Wallet, label: 'Payments' },
        { to: `/projects/${projectId}/analytics`, icon: BarChart3, label: 'Analytics' },
    ] : [];

    return (
        <div className="flex h-screen bg-dark-950">
            {/* Sidebar */}
            <aside
                className={clsx(
                    'fixed inset-y-0 left-0 z-50 flex flex-col bg-dark-900 border-r border-dark-800 transition-all duration-300',
                    sidebarOpen ? 'w-64' : 'w-20'
                )}
            >
                {/* Logo */}
                <div className="flex items-center gap-3 px-6 py-5 border-b border-dark-800">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    {sidebarOpen && (
                        <span className="font-bold text-lg">aiOps</span>
                    )}
                </div>

                {/* Navigation */}
                <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                    {/* Main navigation */}
                    {mainNav.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            className={({ isActive }) =>
                                clsx('sidebar-link', isActive && 'active')
                            }
                        >
                            <item.icon className="w-5 h-5 flex-shrink-0" />
                            {sidebarOpen && <span>{item.label}</span>}
                        </NavLink>
                    ))}

                    {/* Module navigation */}
                    {moduleNav.length > 0 && (
                        <>
                            <div className="pt-4 pb-2">
                                {sidebarOpen && (
                                    <p className="px-4 text-xs font-semibold text-dark-500 uppercase tracking-wider">
                                        Modules
                                    </p>
                                )}
                            </div>
                            {moduleNav.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className={({ isActive }) =>
                                        clsx('sidebar-link', isActive && 'active')
                                    }
                                >
                                    <item.icon className="w-5 h-5 flex-shrink-0" />
                                    {sidebarOpen && <span>{item.label}</span>}
                                </NavLink>
                            ))}
                        </>
                    )}
                </nav>

                {/* Usage indicator */}
                {usageData && sidebarOpen && (
                    <div className="p-3 border-t border-dark-800">
                        <NavLink to="/usage" className="block">
                            <div className="px-3 py-2 rounded-lg bg-dark-800 hover:bg-dark-700 transition-colors">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <Zap className="w-4 h-4 text-primary-400" />
                                        <span className="text-xs font-medium text-dark-300">Credits Today</span>
                                    </div>
                                    <span className="text-xs text-dark-400">
                                        {usageData.creditsUsed?.toLocaleString()} / {usageData.dailyLimit?.toLocaleString()}
                                    </span>
                                </div>
                                <div className="h-1.5 bg-dark-700 rounded-full overflow-hidden">
                                    <div
                                        className={clsx(
                                            'h-full rounded-full transition-all',
                                            (usageData.creditsUsed / usageData.dailyLimit) > 0.9 ? 'bg-red-500' :
                                                (usageData.creditsUsed / usageData.dailyLimit) > 0.7 ? 'bg-yellow-500' :
                                                    'bg-primary-500'
                                        )}
                                        style={{ width: `${Math.min((usageData.creditsUsed / usageData.dailyLimit) * 100, 100)}%` }}
                                    />
                                </div>
                            </div>
                        </NavLink>
                    </div>
                )}

                {/* Collapsed usage indicator */}
                {usageData && !sidebarOpen && (
                    <div className="p-3 border-t border-dark-800">
                        <NavLink to="/usage" className="flex justify-center">
                            <div className="relative">
                                <Zap className={clsx(
                                    'w-5 h-5',
                                    (usageData.creditsUsed / usageData.dailyLimit) > 0.9 ? 'text-red-400' :
                                        (usageData.creditsUsed / usageData.dailyLimit) > 0.7 ? 'text-yellow-400' :
                                            'text-primary-400'
                                )} />
                            </div>
                        </NavLink>
                    </div>
                )}

                {/* User section */}
                <div className="p-3 border-t border-dark-800">
                    <div className={clsx(
                        'flex items-center gap-3 px-3 py-2 rounded-lg bg-dark-800',
                        !sidebarOpen && 'justify-center'
                    )}>
                        <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-sm font-medium">
                            {user?.name?.charAt(0).toUpperCase()}
                        </div>
                        {sidebarOpen && (
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{user?.name}</p>
                                <p className="text-xs text-dark-400 truncate">{user?.subscription?.plan}</p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={logout}
                        className={clsx(
                            'mt-2 w-full sidebar-link text-red-400 hover:bg-red-500/10',
                            !sidebarOpen && 'justify-center'
                        )}
                    >
                        <LogOut className="w-5 h-5" />
                        {sidebarOpen && <span>Logout</span>}
                    </button>
                </div>

                {/* Toggle button */}
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="absolute -right-3 top-8 w-6 h-6 bg-dark-700 rounded-full flex items-center justify-center text-dark-400 hover:text-white border border-dark-600"
                >
                    {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                </button>
            </aside>

            {/* Main content */}
            <main
                className={clsx(
                    'flex-1 overflow-y-auto transition-all duration-300',
                    sidebarOpen ? 'ml-64' : 'ml-20'
                )}
            >
                <div className="p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
